/* ==========================================================================
   Rook Components — Preço/Parcelamento/Pix reativos à calculadora de faixas
   Web Components:
     <rk-price-dominic-element>
     <rk-installments-dominic-element>
     <rk-pix-dominic-element>
   Hidratação 'idle'.

   Cada um lê o preço unitário (centavos) + config em data-attrs e escuta
   rk:faixa:update p/ recalcular sobre o total. O produto é vendido por METRO
   LINEAR, então o total corre sobre os metros publicados pela calculadora
   (faixas × altura da faixa); antes do primeiro cálculo o que aparece é o
   preço de um metro. As fórmulas espelham os snippets Liquid do framework
   (rk-installments / rk-pix-discount).
   Decorators — não editam nenhum arquivo rk-* do framework.
   ========================================================================== */

import { RkComponent } from '@rook/component';
import { formatMoney } from '@theme/money-formatting';

/** Melhor parcela (espelha rk-installments.liquid; aritmética inteira em centavos). */
function bestInstallment(price, { max, noInterest, minValue, rate, withInterest }) {
  const ratePlusOne = 1 + rate;
  const maxCheck = withInterest ? max : noInterest;
  let best = { n: 1, value: price };

  for (let i = 1; i <= maxCheck; i++) {
    let value;
    if (i <= noInterest) {
      value = Math.floor(price / i);
    } else {
      const compound = Math.pow(ratePlusOne, i);
      value = Math.round((price * rate * compound) / (compound - 1));
    }
    if (value >= minValue) {
      best = { n: i, value };
    } else {
      break;
    }
  }
  return best;
}

class RkFaixaBase extends RkComponent {
  static hydration = 'idle';

  setup() {
    this.unit = parseInt(this.dataset.unitPrice || '0', 10) || 0;
    this.compare = parseInt(this.dataset.comparePrice || '0', 10) || 0;
    this.format = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';

    // A calculadora publica o estado no <html> antes de emitir o evento. Ler
    // daqui evita perder o valor quando este bloco hidrata depois dela.
    const publicado = parseInt(document.documentElement.dataset.rkFaixas || '0', 10);
    this.faixas = publicado > 0 ? publicado : 1;
    this.metros = parseFloat(document.documentElement.dataset.rkMetros || '0') || 0;

    this.onUpdate = (event) => {
      const detail = event.detail || {};
      this.faixas = detail.faixas > 0 ? detail.faixas : 1;
      this.metros = detail.metros > 0 ? detail.metros : 0;
      this.render();
    };
    document.addEventListener('rk:faixa:update', this.onUpdate);

    this.render();
  }

  teardown() {
    document.removeEventListener('rk:faixa:update', this.onUpdate);
  }

  money(cents) {
    return formatMoney(cents, this.format, this.currency);
  }

  get total() {
    // Preço por metro linear. Sem metros publicados (nada calculado ainda), o
    // fallback com faixas = 1 devolve justamente o preço de um metro.
    if (this.metros > 0) return Math.round(this.unit * this.metros);
    return this.unit * this.faixas;
  }

  render() {}
}

class RkPriceDominic extends RkFaixaBase {
  render() {
    const totalEl = this.querySelector('[data-role="total"]');
    if (totalEl) totalEl.textContent = this.money(this.total);

    const compareEl = this.querySelector('[data-role="compare"]');
    if (compareEl) {
      if (this.compare > this.unit) {
        const compareTotal =
          this.metros > 0
            ? Math.round(this.compare * this.metros)
            : this.compare * this.faixas;
        compareEl.textContent = this.money(compareTotal);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }
}

class RkInstallmentsDominic extends RkFaixaBase {
  render() {
    const el = this.querySelector('[data-role="installments"]');
    if (!el) return;

    const best = bestInstallment(this.total, {
      max: parseInt(this.dataset.max || '12', 10),
      noInterest: parseInt(this.dataset.noInterest || '3', 10),
      minValue: parseInt(this.dataset.minValue || '500', 10),
      rate: (parseFloat(this.dataset.rate || '0') || 0) / 100,
      withInterest: this.dataset.withInterest === 'true',
    });

    if (best.n > 1) {
      const prefix = this.dataset.prefix || 'em até';
      el.textContent = `${prefix} ${best.n}x de ${this.money(best.value)}`;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }
}

class RkPixDominic extends RkFaixaBase {
  render() {
    const valueEl = this.querySelector('[data-role="pix"]');
    if (!valueEl) return;
    const pct = parseInt(this.dataset.pct || '0', 10) || 0;
    const pix = Math.floor((this.total * (100 - pct)) / 100);
    valueEl.textContent = this.money(pix);
  }
}

if (!customElements.get('rk-price-dominic-element')) {
  customElements.define('rk-price-dominic-element', RkPriceDominic);
}
if (!customElements.get('rk-installments-dominic-element')) {
  customElements.define('rk-installments-dominic-element', RkInstallmentsDominic);
}
if (!customElements.get('rk-pix-dominic-element')) {
  customElements.define('rk-pix-dominic-element', RkPixDominic);
}
